import { useCallback, useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { deleteAccount } from '../../lib/flotiq-client';
import { parseResponseErrors } from '../../lib/flotiq-client/response-errors';
import {
  getMaskedOrganizationName,
  getTestProps,
  resetGtmDataLayer,
} from '../../lib/helpers';
import { detectLanguage } from '../../lib/language';

import Loader from '../../components/Loader/Loader';

import useDarkMode from '../../hooks/useDarkMode';

const testId = 'account-deletion';

const AccountDeletion = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const requestRef = useRef<boolean>(null);
  const [darkMode] = useDarkMode();
  const [, , { removeItem }] = useLocalStorageState('cms.user');
  const { lang } = useParams();
  const targetLang = lang ?? detectLanguage();

  const fetchDeletion = useCallback(
    async (token: string) => {
      if (requestRef.current) return;
      requestRef.current = true;
      try {
        const { body, status, ok } = await deleteAccount(undefined, undefined, {
          token,
        });
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        removeItem();

        TagManager.dataLayer({
          dataLayer: {
            event: 'account_request_delete',
            user_id: body.user_id,
            organization_id: body.organization_id,
            organization_name: getMaskedOrganizationName(
              body.organization_name,
            ),
            space_ids: body.space_ids,
            plan_id: undefined,
            plan_name: undefined,
            space_id: undefined,
          },
        });

        resetGtmDataLayer();

        navigate(`/${targetLang}/login?deleted=true`);
      } catch {
        navigate(`/${targetLang}/login?deleted=false`);
      }
    },
    [navigate, removeItem, targetLang],
  );

  useEffect(() => {
    if (token) {
      fetchDeletion(token);
    }
  }, [token, fetchDeletion]);

  return (
    <>
      <title>{t('Global.RegisterToFlotiq')}</title>
      <div
        className={twMerge(
          'w-full h-screen flex items-center justify-center',
          darkMode && 'dark:bg-slate-900',
        )}
      >
        <Loader
          size="big"
          type="spinner-grid"
          {...getTestProps(testId, 'loader', 'testId')}
        />
      </div>
    </>
  );
};

export default AccountDeletion;
